import { Stack } from "@mui/material";
import { useState } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import type { CrmSyncSettings } from "../../../api/crmConnectorsApi";
import crmConnectorsApi from "../../../api/crmConnectorsApi";
import useClientContext from "../../../context/useClientContext";
import SalesforceLogo from "../../../images/salesforce.png";
import IntegrationPageContainer from "../integration/IntegrationPageContainer";
import CrmActions from "./CrmActions";
import { CrmConnectorContextProvider } from "./CrmConnectorContextProvider";
import CrmManagement from "./CrmManagement";
import CrmSetupInfo from "./CrmSetupInfo";
import FullResyncInfo from "./FullResyncInfo";
import TestConnectionInfo from "./TestConnectionInfo";
import type { FullResyncState, TestConnectionState } from "./crmConnectorState";
import { getInitialFullResyncState, getInitialTestConnectionState } from "./crmConnectorState";

const promotionText =
  "You can use REST API tools to create, manipulate, and search data in Salesforce by sending HTTP requests to endpoints in Salesforce. Depending on where you send requests, you access and operate on different pieces of information, called resources. Resources include records, query results, metadata, and more.";

const SalesforceConnectorPage = () => {
  usePageTitle("Salesforce");

  const { salesforceApp } = useClientContext();

  const [connectionInfo, fetchConnectionError] = useFetch(crmConnectorsApi.salesforce.getConnection);

  const [connectorSettings, fetchSettingsError, { setData: setConnectorSettings }] = useFetch(
    crmConnectorsApi.salesforce.getConnectorSettings
  );

  const [testConnectionState, setTestConnectionState] = useState<TestConnectionState>(() =>
    getInitialTestConnectionState()
  );
  const [fullResyncState, setFullResyncState] = useState<FullResyncState>(getInitialFullResyncState);

  const fetchError = fetchConnectionError || fetchSettingsError;
  if (fetchError) {
    logError(fetchError, "[SalesforceConnectorPage] getConnection");
    return <DataLoadingFailed title="Loading connector settings failed" text={fetchError.message} />;
  }

  const loading = connectionInfo === undefined || connectorSettings === undefined;
  if (loading) {
    return <InlineLoader />;
  }

  const handleSyncEnabledUpdate = (syncEnabled: boolean) => {
    setConnectorSettings({ ...connectorSettings, syncEnabled });
  };

  const handleSyncSettingsUpdate = (syncSettings: CrmSyncSettings) => {
    setConnectorSettings({ ...connectorSettings, syncSettings });
  };

  return (
    <CrmConnectorContextProvider
      crmName="Salesforce"
      crmApiName="salesforce"
      logoSrc={SalesforceLogo}
      connectionInfo={connectionInfo}
      syncEnabled={connectorSettings.syncEnabled}
      onSyncEnabledUpdate={handleSyncEnabledUpdate}
      syncSettings={connectorSettings.syncSettings}
      onSyncSettingsUpdate={handleSyncSettingsUpdate}
      testConnectionState={testConnectionState}
      onTestConnectionStateUpdate={setTestConnectionState}
      fullResyncState={fullResyncState}
      onFullResyncStateUpdate={setFullResyncState}
    >
      <IntegrationPageContainer
        title="Salesforce"
        logoSrc={SalesforceLogo}
        overviewText={promotionText}
        appStatus={salesforceApp?.status}
        maxWidth="xl"
      >
        <CrmSetupInfo />
        <CrmActions />
        <Stack spacing={2} sx={{ width: "48rem" }}>
          <TestConnectionInfo />
          <FullResyncInfo />
        </Stack>
        <CrmManagement />
      </IntegrationPageContainer>
    </CrmConnectorContextProvider>
  );
};

export default SalesforceConnectorPage;
